/**
 * Job CRM Contacts datatable.
 *
 * TanStack-table powered list with column sorting, column visibility,
 * and row click → detail sheet. Mirrors the pattern used by ATO/SRO
 * and the jobs-list datatables.
 *
 * Filtering and free-text search are handled by `ContactsView.tsx` so this
 * component only needs to render whatever rows it is given.
 *
 * Corporate-group view (MOR-708): when `groupView` is not "flat", each
 * group renders as its PRIMARY contact ("top co") as a real parent row —
 * chevron + member count in the name cell — with the remaining member
 * entities nested (indented) beneath it. The cluster anchors where the
 * group's first member lands in the current sort. "collapsed" starts
 * every group closed so only top-level entries (ungrouped contacts +
 * group parents) are visible.
 */

import { useEffect, useMemo, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type Row,
  type SortingState,
  type VisibilityState,
} from "@tanstack/react-table";
import { ChevronDown, ChevronRight } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import type { Id } from "@cvx/_generated/dataModel";
import { cn } from "@/utils/misc";
import { Checkbox } from "@/ui/primitives/checkbox";
import { ColumnVisibilityMenu } from "@/ui/composed/data-display/column-visibility-menu";
import { buildContactsColumns } from "./columns";
import type { CustomJobRole, JobContactRow } from "./types";

const STICKY_SELECT_HEAD =
  "sticky left-0 z-20 bg-background w-[44px] min-w-[44px] px-3";
const STICKY_SELECT_CELL =
  "sticky left-0 z-10 bg-background w-[44px] min-w-[44px] px-3";
const STICKY_HEAD =
  "sticky left-[44px] z-20 bg-background shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)] min-w-[280px]";
const STICKY_CELL =
  "sticky left-[44px] z-10 bg-background shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)] min-w-[280px]";

/**
 * Opaque sticky-cell fills that match translucent row tints.
 *
 * Sticky cells keep `bg-background` so scrolled columns cannot show through.
 * Re-applying `bg-muted/60` (or `bg-primary/5`) on the cell made it
 * see-through. `color-mix` produces the same look as an opaque colour.
 */
const STICKY_FILL_GROUP =
  "bg-[color-mix(in_srgb,hsl(var(--muted))_60%,hsl(var(--background)))] group-hover/contact-row:bg-[color-mix(in_srgb,hsl(var(--muted))_80%,hsl(var(--background)))]";
const STICKY_FILL_SELECTED =
  "bg-[color-mix(in_srgb,hsl(var(--primary))_5%,hsl(var(--background)))] group-hover/contact-row:bg-[color-mix(in_srgb,hsl(var(--primary))_8%,hsl(var(--background)))]";
const STICKY_FILL_HOVER =
  "group-hover/contact-row:bg-[color-mix(in_srgb,hsl(var(--muted))_50%,hsl(var(--background)))]";

function stickyFillClass(isGroupParent: boolean, isSelected: boolean): string {
  if (isGroupParent) return STICKY_FILL_GROUP;
  if (isSelected) return STICKY_FILL_SELECTED;
  return STICKY_FILL_HOVER;
}

function loadVisibility(): VisibilityState {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as VisibilityState;
  } catch {
    /* ignore */
  }
  return {};
}
const STORAGE_KEY = "job-contacts-hidden-cols";

/** How corporate groups are presented in the table. */
export type ContactsGroupView = "expanded" | "collapsed" | "flat";

/**
 * One renderable unit: either a plain contact row, or a group header
 * followed (when expanded) by its member rows.
 */
type DisplayUnit =
  | { kind: "row"; row: Row<JobContactRow> }
  | {
      kind: "group";
      groupId: string;
      /** The group's primary contact ("top co") — rendered as the parent row. */
      primary: Row<JobContactRow>;
      /** Remaining members, nested under the parent when expanded. */
      children: Array<Row<JobContactRow>>;
    };

interface ContactsDataTableProps {
  rows: Array<JobContactRow>;
  selectedContactId: Id<"jobContacts"> | null;
  onSelectContact: (contact: JobContactRow) => void;
  selectedIds: Set<Id<"jobContacts">>;
  onToggleSelected: (id: Id<"jobContacts">) => void;
  onToggleAll: (select: boolean) => void;
  /** Group presentation mode; owned by ContactsView's toolbar select. */
  groupView: ContactsGroupView;
  /** Org custom roles, keyed by id — used to render custom role badges. */
  customRolesById: ReadonlyMap<string, CustomJobRole>;
}

export function ContactsDataTable({
  rows,
  selectedContactId,
  onSelectContact,
  selectedIds,
  onToggleSelected,
  onToggleAll,
  groupView,
  customRolesById,
}: ContactsDataTableProps) {
  const [sorting, setSorting] = useState<SortingState>([
    { id: "totalExposure", desc: true },
  ]);
  const [columnVisibility, setColumnVisibility] =
    useState<VisibilityState>(loadVisibility);
  // Per-group expansion overrides on top of the groupView default. Reset
  // whenever the view mode changes so the select behaves predictably.
  const [expansionOverrides, setExpansionOverrides] = useState<
    Map<string, boolean>
  >(() => new Map());
  useEffect(() => {
    setExpansionOverrides(new Map());
  }, [groupView]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(columnVisibility),
      );
    } catch {
      /* ignore */
    }
  }, [columnVisibility]);

  const columns = useMemo(
    () => buildContactsColumns(customRolesById),
    [customRolesById],
  );

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const visibleColCount = table.getVisibleFlatColumns().length + 1;
  const sortedRows = table.getRowModel().rows;

  // Cluster sorted rows into display units. A group is anchored at its
  // first member's sorted position; later members are pulled up to it so
  // the group renders contiguously without fighting the active sort.
  const displayUnits = useMemo<DisplayUnit[]>(() => {
    if (groupView === "flat") {
      return sortedRows.map((row) => ({ kind: "row", row }));
    }
    const membersByGroup = new Map<string, Array<Row<JobContactRow>>>();
    for (const row of sortedRows) {
      const groupId = row.original.groupId;
      if (!groupId) continue;
      const list = membersByGroup.get(groupId) ?? [];
      list.push(row);
      membersByGroup.set(groupId, list);
    }
    const emitted = new Set<string>();
    const units: DisplayUnit[] = [];
    for (const row of sortedRows) {
      const groupId = row.original.groupId;
      if (!groupId) {
        units.push({ kind: "row", row });
        continue;
      }
      if (emitted.has(groupId)) continue;
      emitted.add(groupId);
      const members = membersByGroup.get(groupId) ?? [row];
      // A group with a single visible member renders as a plain row —
      // a one-member cluster is just noise.
      if (members.length === 1) {
        units.push({ kind: "row", row: members[0] });
        continue;
      }
      // The primary contact ("top co") is the parent; everyone else
      // nests under it. Fall back to the first-sorted member when no
      // primary is flagged.
      const primary =
        members.find((m) => m.original.isGroupPrimary) ?? members[0];
      units.push({
        kind: "group",
        groupId,
        primary,
        children: members.filter((m) => m !== primary),
      });
    }
    return units;
  }, [sortedRows, groupView]);

  const isGroupExpanded = (groupId: string): boolean =>
    expansionOverrides.get(groupId) ?? groupView === "expanded";

  const toggleGroupExpanded = (groupId: string) => {
    setExpansionOverrides((prev) => {
      const next = new Map(prev);
      next.set(groupId, !isGroupExpanded(groupId));
      return next;
    });
  };

  const visibleSelectedCount = sortedRows.reduce(
    (n, row) => (selectedIds.has(row.original._id) ? n + 1 : n),
    0,
  );
  const allVisibleSelected =
    sortedRows.length > 0 && visibleSelectedCount === sortedRows.length;
  const someVisibleSelected =
    visibleSelectedCount > 0 && visibleSelectedCount < sortedRows.length;

  const renderContactRow = (
    row: Row<JobContactRow>,
    options?: {
      /** Nested member of a group — indents the name cell. */
      inGroup?: boolean;
      /**
       * Parent ("top co") of a group — renders an expand chevron and a
       * member count before the name. Clicking the chevron toggles the
       * group; clicking the row still opens the contact.
       */
      groupControl?: {
        groupId: string;
        expanded: boolean;
        childCount: number;
      };
    },
  ) => {
    const isSelected = selectedContactId === row.original._id;
    const isChecked = selectedIds.has(row.original._id);
    const groupControl = options?.groupControl;
    const Chevron = groupControl?.expanded ? ChevronDown : ChevronRight;
    return (
      <TableRow
        key={row.id}
        className={cn(
          "group/contact-row cursor-pointer transition-colors",
          isSelected ? "hover:bg-primary/8 bg-primary/5" : "hover:bg-muted/50",
          // Group styling: ONLY the parent ("top co") row is shaded —
          // neutral muted tokens so it darkens in light mode and lightens
          // in dark mode. Nested members keep the normal row background
          // and are distinguished by indentation alone.
          groupControl &&
            "border-l-[3px] border-l-muted-foreground/60 bg-muted/60 hover:bg-muted/80",
        )}
        onClick={() => onSelectContact(row.original)}
      >
        <TableCell
          className={cn(
            "py-3",
            // Group-header rows are two-line (name + "+ N more entities"), and
            // the chevron/name column top-anchors its content. Match that here
            // so the checkbox lines up with the group name's first line instead
            // of floating to the row's vertical centre.
            groupControl ? "align-top" : "align-middle",
            STICKY_SELECT_CELL,
            stickyFillClass(Boolean(groupControl), isSelected),
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            aria-label={`Select ${row.original.displayName}`}
            checked={isChecked}
            onCheckedChange={() => onToggleSelected(row.original._id)}
            // Nudge the checkbox down so its centre aligns with the chevron
            // icon's centre in the sibling name cell (chevron button has
            // `mt-0.5` + `p-0.5`, so its icon sits ~4px below the cell's
            // top-padding edge; `mt-1` puts the 16px checkbox at the same
            // vertical centre).
            className={cn(groupControl && "mt-1")}
          />
        </TableCell>
        {row.getVisibleCells().map((cell) => (
          <TableCell
            key={cell.id}
            className={cn(
              "px-4 py-3 align-top",
              cell.column.id === "totalExposure" && "text-right",
              cell.column.id === "displayName" && STICKY_CELL,
              cell.column.id === "displayName" &&
                stickyFillClass(Boolean(groupControl), isSelected),
              // Indent grouped members' name cells so the hierarchy reads
              // at a glance.
              cell.column.id === "displayName" && options?.inGroup && "pl-9",
            )}
          >
            {cell.column.id === "displayName" && groupControl ? (
              <div className="flex items-start gap-1.5">
                <button
                  type="button"
                  aria-label={
                    groupControl.expanded
                      ? "Collapse group entities"
                      : `Show ${groupControl.childCount} more group entities`
                  }
                  className="mt-0.5 rounded bg-muted-foreground/15 p-0.5 text-foreground/70 hover:bg-muted-foreground/25 hover:text-foreground"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleGroupExpanded(groupControl.groupId);
                  }}
                >
                  <Chevron className="h-4 w-4" />
                </button>
                <div className="min-w-0 flex-1">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  {!groupControl.expanded && (
                    <div className="mt-0.5 text-[11px] text-muted-foreground">
                      + {groupControl.childCount} more{" "}
                      {groupControl.childCount === 1 ? "entity" : "entities"} in
                      this group
                    </div>
                  )}
                </div>
              </div>
            ) : (
              flexRender(cell.column.columnDef.cell, cell.getContext())
            )}
          </TableCell>
        ))}
      </TableRow>
    );
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-end px-1">
        <ColumnVisibilityMenu table={table} />
      </div>
      <div className="overflow-hidden rounded-xl border border-border bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]">
        <div className="max-h-[calc(100vh-300px)] overflow-auto">
          <Table className="w-full min-w-[1000px]">
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow
                  key={headerGroup.id}
                  className="bg-muted/30 hover:bg-transparent"
                >
                  <TableHead
                    className={cn("h-10 align-middle", STICKY_SELECT_HEAD)}
                  >
                    <Checkbox
                      aria-label="Select all contacts"
                      checked={
                        allVisibleSelected
                          ? true
                          : someVisibleSelected
                            ? "indeterminate"
                            : false
                      }
                      onCheckedChange={(checked) =>
                        onToggleAll(checked === true)
                      }
                    />
                  </TableHead>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className={cn(
                        "h-10 whitespace-nowrap text-xs font-semibold uppercase tracking-wider text-muted-foreground",
                        header.id === "totalExposure" && "text-right",
                        header.id === "displayName" && STICKY_HEAD,
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {displayUnits.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={visibleColCount}
                    className="h-32 text-center"
                  >
                    <span className="text-sm text-muted-foreground">
                      No contacts match the current filters.
                    </span>
                  </TableCell>
                </TableRow>
              ) : (
                displayUnits.flatMap((unit) => {
                  if (unit.kind === "row") {
                    return [renderContactRow(unit.row)];
                  }
                  const expanded = isGroupExpanded(unit.groupId);
                  const parent = renderContactRow(unit.primary, {
                    groupControl: {
                      groupId: unit.groupId,
                      expanded,
                      childCount: unit.children.length,
                    },
                  });
                  if (!expanded) return [parent];
                  return [
                    parent,
                    ...unit.children.map((row) =>
                      renderContactRow(row, { inGroup: true }),
                    ),
                  ];
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
